import { useEffect, useState } from "react";
import api from "../lib/api";
import { Users, TrendingUp, ShoppingBag, Award, ShieldAlert, AlertCircle } from "lucide-react";

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5 text-gold-700" />
      <span className="font-mono text-xs text-muted2 uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-display text-3xl text-ink-900">{value}</div>
    {sub && <div className="text-xs text-muted2 mt-1">{sub}</div>}
  </div>
);

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [security, setSecurity] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then(r => setOverview(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
    api.get("/admin/purchases").then(r => setPurchases(r.data));
    api.get("/admin/security-log").then(r => setSecurity(r.data));
  }, []);

  const tabs = [
    { id: "overview", label: "Vue d'ensemble" },
    { id: "users", label: "Utilisateurs" },
    { id: "purchases", label: "Ventes" },
    { id: "security", label: "Sécurité" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-3">Administration</div>
      <h1 className="font-display text-4xl text-ink-900">Console E.C.S</h1>

      <div className="flex gap-1 mt-6 mb-6 border-b border-ink-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm transition-colors whitespace-nowrap ${
              tab === t.id ? "text-gold-700 border-b-2 border-gold" : "text-muted2 hover:text-ink-900"
            }`}
            data-testid={`admin-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Metric icon={Users} label="utilisateurs" value={overview.total_users} testid="metric-users" />
          <Metric icon={ShoppingBag} label="ventes" value={overview.total_purchases} testid="metric-purchases" />
          <Metric icon={Award} label="certificats" value={overview.total_certificates} testid="metric-certificates" />
          <Metric icon={TrendingUp} label="Revenu total" value={`${overview.revenue_all_time.toFixed(2)}€`} testid="metric-revenue-total" />
          <Metric icon={TrendingUp} label="Ce mois-ci" value={`${overview.revenue_month.toFixed(2)}€`} testid="metric-revenue-month" />
          <Metric icon={TrendingUp} label="Aujourd'hui" value={`${overview.revenue_today.toFixed(2)}€`} testid="metric-revenue-today" />
        </div>
      )}

      {tab === "users" && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="text-xs uppercase tracking-widest text-muted2 border-b border-ink-200">
              <tr>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Rôle</th>
                <th className="text-left px-4 py-3">Créé le</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-ink-100 hover:bg-gold-500/5">
                  <td className="px-4 py-3 text-ink-900">{u.full_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted2">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.is_admin ? <span className="px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-700 text-xs">Admin</span>
                     : <span className="text-muted2 text-xs">Utilisateur</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted2">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "purchases" && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-purchases-table">
            <thead className="text-xs uppercase tracking-widest text-muted2 border-b border-ink-200">
              <tr>
                <th className="text-left px-4 py-3">Payment ID</th>
                <th className="text-left px-4 py-3">Montant</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(p => (
                <tr key={p.id} className="border-b border-ink-100">
                  <td className="px-4 py-3 font-mono text-xs text-muted2">{p.stripe_payment_intent_id}</td>
                  <td className="px-4 py-3 text-gold-700">{p.amount_euros.toFixed(2)}€</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "succeeded" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted2">{new Date(p.purchased_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
              {purchases.length === 0 && (
                <tr><td colSpan="4" className="text-center text-muted2 py-10">Aucune vente pour l'instant</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "security" && security && (
        <div>
          <div className="glass rounded-2xl p-6 flex items-start gap-3 mb-4">
            <ShieldAlert className="w-6 h-6 text-gold-700 flex-shrink-0" />
            <div>
              <div className="font-display text-lg text-ink-900">Centre de sécurité</div>
              <div className="text-sm text-muted2 mt-1">
                Tentatives de quiz marquées comme suspectes (≥ 3 changements d'onglet).
              </div>
            </div>
          </div>
          {security.flagged_attempts.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-muted2 text-sm">
              <AlertCircle className="w-6 h-6 mx-auto text-success mb-2" />
              Aucune tentative suspecte détectée. Tout va bien.
            </div>
          ) : (
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted2 border-b border-ink-200">
                  <tr>
                    <th className="text-left px-4 py-3">User ID</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Tabs</th>
                    <th className="text-left px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {security.flagged_attempts.map(a => (
                    <tr key={a.id} className="border-b border-ink-100">
                      <td className="px-4 py-3 font-mono text-xs">{a.user_id.slice(0,8)}…</td>
                      <td className="px-4 py-3">{a.score_percent}%</td>
                      <td className="px-4 py-3 text-warning">{a.tab_switch_count}</td>
                      <td className="px-4 py-3 text-xs text-muted2">{new Date(a.completed_at).toLocaleString("fr-FR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
